
import React, { useState } from 'react';
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from 'react-router-dom';
import { useRecaptcha } from '@/hooks/useRecaptcha';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ScanIntroHero from '@/components/telemarketing-scan/ScanIntroHero';
import ScanProgressBar from '@/components/telemarketing-scan/ScanProgressBar';
import ScanQuestionCard from '@/components/telemarketing-scan/ScanQuestionCard';
import ScanContactForm, { formSchema } from '@/components/telemarketing-scan/ScanContactForm';
import { 
  useTelemarketingScan, 
  getScoreInterpretation,
  scanQuestions, 
  scoreOptions 
} from '@/hooks/useTelemarketingScan';
import { useScanResultSubmission, ScanFormData } from '@/hooks/useScanResultSubmission';

const TelemarketingScan = () => {
  // Add navigate for redirection
  const navigate = useNavigate();
  
  // State for UI flow
  const [showContactForm, setShowContactForm] = useState(false);
  
  // Initialize reCAPTCHA
  useRecaptcha();
  
  // Use our scan hook
  const {
    currentTheme,
    currentQuestionIndex,
    answers,
    scanCompleted,
    totalScore,
    maxScore,
    isScanning,
    animateCard,
    startScan,
    handleAnswerSelect,
    moveToNextQuestion,
    getCurrentQuestionKey,
    getProgressPercentage,
    getScorePercentage
  } = useTelemarketingScan();

  // Setup contact form with updated schema that includes honeypot
  const form = useForm<ScanFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      company: "",
      email: "",
      website: "", // Initialize honeypot field as empty
    },
  });

  // Handle scan result submission with navigation to results page
  const { submitResults } = useScanResultSubmission(
    totalScore,
    maxScore,
    () => {
      // Calculate score percentage and other details
      const scorePercentage = getScorePercentage();
      const interpretation = getScoreInterpretation(scorePercentage);
      const categoryScores = calculateCategoryScores();
      
      // Navigate to results page with scan data
      navigate('/telemarketing-scan/results', {
        state: {
          score: totalScore,
          maxScore: maxScore,
          scorePercentage: scorePercentage,
          interpretation: interpretation,
          categoryScores: categoryScores
        }
      });
    }
  );

  // Handle form submission
  const onSubmit = (data: ScanFormData) => {
    submitResults(data);
  };
  
  // When scan completes, show contact form
  if (scanCompleted && !showContactForm) {
    setShowContactForm(true);
  }
  
  // Calculate current question details
  const currentQuestion = currentTheme 
    ? scanQuestions[currentTheme][currentQuestionIndex] 
    : "";
  
  const totalQuestionsInTheme = currentTheme
    ? scanQuestions[currentTheme].length
    : 0;
    
  const isLastQuestion = 
    currentTheme && 
    currentQuestionIndex === scanQuestions[currentTheme].length - 1 && 
    Object.keys(scanQuestions).indexOf(currentTheme) === Object.keys(scanQuestions).length - 1;

  // Calculate category scores
  const calculateCategoryScores = () => {
    const categoryScores: Record<string, { score: number, maxScore: number }> = {};
    
    Object.keys(scanQuestions).forEach(theme => {
      let themeScore = 0;
      let themeMaxScore = scanQuestions[theme].length * Math.max(...scoreOptions.map(opt => opt.value));
      
      // Calculate score for this theme
      scanQuestions[theme].forEach((_, questionIndex) => {
        const questionKey = `${theme}_${questionIndex}`;
        if (answers[questionKey] !== undefined) {
          themeScore += answers[questionKey];
        }
      });
      
      categoryScores[theme] = {
        score: themeScore,
        maxScore: themeMaxScore
      };
    });
    
    return categoryScores;
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-grow pt-16">
        {/* Intro Hero Section - shown when not scanning */}
        {!isScanning && (
          <ScanIntroHero onStartScan={startScan} />
        )}
        
        {/* Scan Interface - shown when scanning but not completed */}
        {isScanning && !scanCompleted && (
          <section className="py-8 bg-gradient-to-b from-white to-gray-50">
            <div className="container max-w-4xl pt-8">
              {/* Progress bar component */}
              <ScanProgressBar 
                progressPercentage={getProgressPercentage()}
                currentTheme={currentTheme}
                allThemes={Object.keys(scanQuestions)}
              />
              
              {/* Question card component */}
              <ScanQuestionCard
                theme={currentTheme}
                question={currentQuestion}
                questionIndex={currentQuestionIndex}
                totalQuestions={totalQuestionsInTheme}
                currentAnswer={answers[getCurrentQuestionKey()]}
                scoreOptions={scoreOptions}
                onAnswerSelect={(value) => handleAnswerSelect(getCurrentQuestionKey(), value)}
                onNextQuestion={moveToNextQuestion}
                isLastQuestion={isLastQuestion}
                animateCard={animateCard}
              />
            </div>
          </section>
        )}
        
        {/* Contact Form - shown after scan completion */}
        {showContactForm && (
          <ScanContactForm
            form={form}
            onSubmit={onSubmit}
          />
        )}
      </main>
      
      <Footer />
    </div>
  );
};

export default TelemarketingScan;
